import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';

const MAX_OPERATIONS = 6;

const Calculator = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { type } = useParams();
    const initialInputData = {
        runningHours: '',
        stackDiameter: '',
        flueGasVelocity: '',
        pollutantType: 'SPM', // Default pollutant type
        pollutantConcentration: '',
        totalDays: '365' // Set default value to 365
    };
    const [inputData, setInputData] = useState(initialInputData);
    const [result, setResult] = useState(null);
    const userInfo = location.state;
    const userKey = `${userInfo.email}-${type}`;

    useEffect(() => {
        if (!userInfo) {
            navigate(`/user-info/${type}`);
        }
    }, [userInfo, navigate, type]);

    const handleInputChange = (e) => {
        setInputData({
            ...inputData,
            [e.target.name]: e.target.value
        });
    };

    const performCalculations = () => {
        const userOps = JSON.parse(localStorage.getItem(userKey)) || { count: 0 };

        if (userOps.count >= MAX_OPERATIONS) {
            alert('You have reached the maximum number of operations.');
            return;
        }

        const { runningHours, stackDiameter, flueGasVelocity, pollutantConcentration, totalDays } = inputData;

        const stackDiameterM = parseFloat(stackDiameter);
        const flueGasVelocityMps = parseFloat(flueGasVelocity);
        const runningHoursHrs = parseFloat(runningHours);
        const pollutantConcentrationMgNm3 = parseFloat(pollutantConcentration);
        const totalDaysYear = parseFloat(totalDays);

        if (isNaN(stackDiameterM) || isNaN(flueGasVelocityMps) || isNaN(runningHoursHrs) || isNaN(pollutantConcentrationMgNm3) || isNaN(totalDaysYear)) {
            alert('Please enter valid numbers for all fields.');
            return;
        }

        const flowRate = Math.PI * Math.pow(stackDiameterM / 2, 2) * flueGasVelocityMps;
        const totalEmissionKg = (runningHoursHrs * pollutantConcentrationMgNm3 * flowRate * 3600) / (1000 * 1000); // Result in Kg
        const averageEmissionKgPerDay = totalEmissionKg / totalDaysYear; // Result in Kg/day

        setResult({
            totalEmissionKg: totalEmissionKg.toFixed(5),
            averageEmissionKgPerDay: averageEmissionKgPerDay.toFixed(5)
        });

        // Update the count in local storage
        userOps.count += 1;
        localStorage.setItem(userKey, JSON.stringify(userOps));
    };

    const resetForm = () => {
        setInputData(initialInputData);
        setResult(null);
    };

    const downloadPDF = () => {
        if (!result) {
            alert('Please perform the calculation first.');
            return;
        }

        const userOps = JSON.parse(localStorage.getItem(userKey)) || { count: 0 };

        if (userOps.count >= MAX_OPERATIONS) {
            alert('You have reached the maximum number of operations.');
            return;
        }

        const doc = new jsPDF();

        // Add watermark
        doc.setFontSize(80); // Increase font size for watermark
        doc.setTextColor(220, 220, 220);
        doc.text('EPTECH', 40, 100, { angle: 45 }); // Adjust position for diagonal placement

        // Add title
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Air Pollutants Emitted to Environment', 105, 20, null, null, 'center');

        // Add user info
        doc.setFontSize(16);
        doc.setFont('helvetica', 'normal');
        doc.text(`Name: ${userInfo.name}`, 10, 40);
        doc.text(`Email: ${userInfo.email}`, 10, 50);

        // Draw a line
        doc.setLineWidth(0.5);
        doc.line(10, 55, 200, 55);

        // Add input data
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Inputs', 10, 65);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'normal');
        Object.keys(inputData).forEach((field, index) => {
            const fieldName = field
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, (str) => str.toUpperCase());
            const unit = getUnit(field);
            doc.text(`${fieldName}: ${inputData[field]} ${unit}`, 10, 75 + index * 10);
        });

        // Draw a line
        doc.setLineWidth(0.5);
        doc.line(10, 75 + Object.keys(inputData).length * 10 + 10, 200, 75 + Object.keys(inputData).length * 10 + 10);

        // Add result
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Result', 10, 75 + Object.keys(inputData).length * 10 + 20);

        doc.setFontSize(14);
        doc.setFont('helvetica', 'normal');
        const resultText = `The ${inputData.pollutantType} emitted to the environment for the total running hours (${inputData.runningHours} Hrs) is ${result.totalEmissionKg} Kg. The average emission is ${result.averageEmissionKgPerDay} Kg/day.`;
        doc.text(resultText, 10, 75 + Object.keys(inputData).length * 10 + 30, { maxWidth: 180 });

        doc.save('report.pdf');

        // Update the count in local storage
        userOps.count += 1;
        localStorage.setItem(userKey, JSON.stringify(userOps));
    };

    const getUnit = (field) => {
        switch (field) {
            case 'runningHours':
                return 'Hrs';
            case 'stackDiameter':
                return 'm';
            case 'flueGasVelocity':
                return 'm/s';
            case 'pollutantConcentration':
                return 'mg/Nm^3';
            case 'totalDays':
                return 'days';
            default:
                return '';
        }
    };

    return (
        <div className="min-h-screen flex flex-col md:flex-row bg-gray-50 p-4">
            <div className="w-full md:w-1/2 p-4">
                <h2 className="text-4xl font-bold text-center mb-4 text-dark-green">CALCULATE</h2>
                <form className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
                    {[
                        { label: 'Running Hours (Hrs)', name: 'runningHours', placeholder: 'Enter the Running hours' },
                        { label: 'Stack Diameter (m)', name: 'stackDiameter', placeholder: 'Enter the Stack diameter' },
                        { label: 'Flue Gas Velocity (m/s)', name: 'flueGasVelocity', placeholder: 'Enter the Flue Gas Velocity' },
                        { label: 'Type of Pollutant', name: 'pollutantType', placeholder: 'Select the Type of Pollutant', type: 'dropdown' },
                        { label: 'Pollutant Concentration (mg/Nm^3)', name: 'pollutantConcentration', placeholder: 'Enter the Pollutant Concentration' },
                        { label: 'Total Days in Financial Year (Change as per days in your F.Y.)', name: 'totalDays', placeholder: 'Enter the Total Days in Financial Year' }
                    ].map(({ label, name, placeholder, type }) => (
                        <div key={name} className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">{label}</label>
                            {type === 'dropdown' ? (
                                <select
                                    name={name}
                                    value={inputData[name]}
                                    onChange={handleInputChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                >
                                    {['SPM', 'SOX', 'NOX', 'CO', 'others'].map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="number"
                                    name={name}
                                    value={inputData[name]}
                                    onChange={handleInputChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus                                    :shadow-outline"
                                    placeholder={placeholder}
                                />
                            )}
                        </div>
                    ))}
                    <div className="flex items-center justify-center space-x-4">
                        <button
                            type="button"
                            onClick={performCalculations}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        >
                            Calculate
                        </button>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        >
                            Reset
                        </button>
                    </div>
                </form>
            </div>
            <div className="w-full md:w-1/2 p-4 mt-8 md:mt-0">
                <div className="bg-white shadow-md rounded p-4 w-full">
                    <h2 className="text-4xl font-bold text-center mb-4 text-dark-green">Pollutant Emission Report</h2>
                    {userInfo && (
                        <div className="mb-4 text-center">
                            <p className="text-xl"><strong>Name:</strong> {userInfo.name}</p>
                            <p className="text-xl"><strong>Email:</strong> {userInfo.email}</p>
                        </div>
                    )}
                    <h3 className="text-2xl font-bold mb-2">Inputs</h3>
                    {Object.keys(inputData).map((field, index) => (
                        <p key={index} className="text-lg">{`${field.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}: ${inputData[field]} ${getUnit(field)}`}</p>
                    ))}
                    {result !== null && (
                        <>
                            <h3 className="text-2xl font-bold mt-4">Result</h3>
                            <p className="text-lg">Total Emission: {result.totalEmissionKg} Kg</p>
                            <p className="text-lg">Average Emission: {result.averageEmissionKgPerDay} Kg/day</p>
                        </>
                    )}
                    <div className="flex items-center justify-center mt-4">
                        <button
                            onClick={downloadPDF}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                            type="button"
                        >
                            Download Report
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Calculator;
